import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { Payment } from '@/features/ui';
import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';
import { DeliveryAddress } from '@/features/ui/Steps/DeliveryAddress/DeliveryAddress';

import { InvoiceStatus } from '@/shared/types';

import { useFormDraftTools } from '@/hooks/useFormDraftTools';
import { usePaymentShortPolling } from '@/hooks/usePaymentShortPolling';

import AuthContext from '@/contexts/Auth/AuthContext';
import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { UserService, FormDataType, UserRequest, UserServicePaymentType } from '@/api/types';

import api from '@/api';

import { SERVICE_CODE, STEP_NUMBER_TO_PAY } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { deliveryAddressSchema, extractRedeliveryPersonalDataSchema } from './model/schemas';
import { ExtractRedeliveryMergedSchema, ServiceProps } from './model/types';
import { DataPreview, ExtractRedeliveryResult } from './ui/steps';
import { ExtractRedeliveryPersonalData } from './ui/steps/ExtractRedeliveryPersonalData';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Особисті та паспортні дані',
    step: ExtractRedeliveryPersonalData,
    validationSchema: extractRedeliveryPersonalDataSchema,
  },
  { title: 'Доставка', step: DeliveryAddress, validationSchema: deliveryAddressSchema },
  { title: 'Підтвердження даних', step: DataPreview, validationSchema: confirmationOfApplicationSchema },
  {
    title: 'Оплата',
    step: Payment,
  },
  { title: 'Результат', step: ExtractRedeliveryResult },
];

export const MissingPersonExtract: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const { userData } = useContext(AuthContext);
  const [stepToGo, setStepToGo] = useState<Nullable<number>>(null);
  const { state } = useLocation();
  const navigate = useNavigate();

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;
  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);

  const { isLoading, currentRequestId, onPaymentContinue } = useFormDraftTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => setDefaultValues(data ?? {}),
    stepNumberToPay: STEP_NUMBER_TO_PAY,
  });
  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const [parentId, setParentId] = useState<Nullable<string>>(null);
  const [redirectServiceInfo, setRedirectServiceInfo] = useState<{
    link: string;
    request_id: number;
  }>();

  useEffect(() => {
    if (state?.requestId && !parentId) {
      setParentId(state.requestId);
      return;
    }

    if (!parentId && !state?.continueGercPayment && !state?.continuePayment) navigate(-1);

    !state?.continueGercPayment &&
      !state?.continuePayment &&
      api.erz.services
        .checkRedeliveryState(parentId ?? '')
        .then(({ data }) => {
          if (!data) {
            navigate(-1);
            return;
          }

          if (data.status === 'payment') {
            setStepToGo(STEP_NUMBER_TO_PAY);
          }
        })
        .catch((error) => {
          console.error(error);
          navigate(-1);
        });
  }, [parentId]);

  const onGercPaymentContinue = async (userRequestId: UserRequest['id']): Promise<number> => {
    if (sessionStorage.getItem('redirectServiceInfo')) {
      setRedirectServiceInfo(JSON.parse(sessionStorage.getItem('redirectServiceInfo') as string));
      sessionStorage.removeItem('redirectServiceInfo');
    }

    startPaymentShortPolling(userRequestId);

    return onPaymentContinue(userRequestId);
  };

  const submitForm = async (formData: ExtractRedeliveryMergedSchema): Promise<any> =>
    api.services.submit(
      SERVICE_CODE,
      await generatePayload({
        withAttachments: true,
        formData,
        requestId: currentRequestId ?? parentId ?? '',
        service: currentService,
        userData,
      }),
      String(formData.allowProfileUpdate),
    );

  return (
    <ServiceFormTemplate
      serviceCode={SERVICE_CODE}
      title={currentService?.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onGercPaymentContinue={onGercPaymentContinue}
      onPaymentContinue={onPaymentContinue}
      skipAvailabilityValidation={(step) => step === 1}
      stepProps={{
        userRequestId: currentRequestId ?? currentUserRequest?.id ?? parentId ?? '',
        invoiceStatus: currentUserRequest?.invoice?.status ?? InvoiceStatus.IN_PROGRESS,
        submitForm,
        stepToGo,
        paymentShortPolling: { isShortPollingInProgress },
        finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
        allowedPaymentType: currentService.paymentType ?? UserServicePaymentType.WITHOUT_PAYMENT,
        beforeRedirectToGerc: () => {
          if (redirectServiceInfo) {
            sessionStorage.setItem('redirectServiceInfo', JSON.stringify(redirectServiceInfo));
          }
        },
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
      }}
    />
  );
};
